import { useEffect, useRef, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "i18n-fe";
import { sendStationSnapshotConfirmed } from "../hooks/useStationSnapshotUpload";

// #3269 - the Queue page's manual "Send snapshot now". It confirms first, then
// shows loading and the real outcome. sendStationSnapshotConfirmed() (#3264)
// is the one uploader: it resolves { ok } and never rejects, so a failure here
// is only ever this dialog's own failure state. It never touches AuthGate,
// never blocks scoring, never toasts outside the dialog (kiosk doctrine). The
// automatic background snapshot stays silent; only this user-started send
// reports.
//
// Mounted only while open (Queue.jsx), so every open starts at "confirm".
// While sending, the dialog cannot be dismissed and the buttons are disabled.
export default function SendSnapshotDialog({ onClose }) {
  const { t } = useTranslation("score");
  // { phase: "confirm" | "sending" | "success" | "failure", reason }
  const [state, setState] = useState({ phase: "confirm" });
  const alive = useRef(true);
  const inFlight = useRef(false);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const send = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setState({ phase: "sending" });
    let result;
    try {
      result = await sendStationSnapshotConfirmed();
    } catch {
      result = { ok: false, reason: "uploadFailed" };
    }
    inFlight.current = false;
    if (!alive.current) return;
    setState(
      result?.ok
        ? { phase: "success" }
        : { phase: "failure", reason: result?.reason },
    );
  };

  const { phase, reason } = state;
  const sending = phase === "sending";
  let body;
  let actions;
  if (phase === "confirm" || sending) {
    body = (
      <Stack spacing={2}>
        <Typography>{t("queuePage.snapshotDialog.explain")}</Typography>
        <Typography>{t("queuePage.snapshotDialog.noScoreChange")}</Typography>
        {sending && (
          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress size={24} data-testid="snapshot-sending-spinner" />
            <Typography>{t("queuePage.snapshotDialog.sending")}</Typography>
          </Stack>
        )}
      </Stack>
    );
    actions = (
      <>
        <Button onClick={onClose} disabled={sending}>
          {t("queuePage.snapshotDialog.cancel")}
        </Button>
        <Button variant="contained" onClick={send} disabled={sending}>
          {t("queuePage.snapshotDialog.send")}
        </Button>
      </>
    );
  } else if (phase === "success") {
    body = <Typography>{t("queuePage.snapshotDialog.success")}</Typography>;
    actions = (
      <Button onClick={onClose}>{t("queuePage.snapshotDialog.close")}</Button>
    );
  } else {
    body = (
      <Typography>
        {reason === "noUploader"
          ? t("queuePage.snapshotDialog.noUploader")
          : t("queuePage.snapshotDialog.failure")}
      </Typography>
    );
    actions = (
      <>
        <Button onClick={onClose}>{t("queuePage.snapshotDialog.close")}</Button>
        <Button variant="contained" onClick={send}>
          {t("queuePage.snapshotDialog.tryAgain")}
        </Button>
      </>
    );
  }

  return (
    <Dialog
      open
      onClose={sending ? undefined : onClose}
      data-testid="send-snapshot-dialog"
    >
      <DialogTitle>{t("queuePage.snapshotDialog.title")}</DialogTitle>
      <DialogContent>{body}</DialogContent>
      <DialogActions>{actions}</DialogActions>
    </Dialog>
  );
}
